import { useLayoutEffect, useRef, useState } from 'react';

/** Gap between the anchor edge and the bubble box (the tip arrow lives inside the box). */
export const COACH_MARK_GAP = 4;
/** Minimum distance from the viewport edges. */
export const COACH_MARK_MARGIN = 8;
/**
 * Offset of the tip centre from the bubble's edge: horizontally for `start` /
 * `end` alignment, and the closest the tip may get to the top/bottom corners
 * of a beside-bubble (12px inset + half of the 16px tip).
 */
export const COACH_MARK_TIP_INSET = 20;

/** Sides to try, in order, when the preferred one does not fit: opposite first, then the perpendicular pair. */
const FALLBACKS = {
    top: ['bottom', 'right', 'left'],
    bottom: ['top', 'right', 'left'],
    left: ['right', 'top', 'bottom'],
    right: ['left', 'top', 'bottom'],
};
const VERTICAL_SIDES = new Set(['top', 'bottom']);

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Pure placement maths, exported for tests. Returns `{ top, left, placement }`
 * in viewport coordinates, plus `tipOffset` for a bubble beside its anchor:
 * how far down the bubble's edge the tip sits, in px.
 *
 * `viewport` is the box the bubble must stay inside — the window by default,
 * or the page scroller's visible box (`{ top, left, width, height }`, in
 * viewport coordinates) when the mark renders inside it.
 *
 * A side "fits" when the bubble stays inside the viewport along that side's
 * own axis (vertically for top/bottom, horizontally for left/right); overflow
 * on the other axis is solved by sliding the bubble, which keeps it on the
 * side the design asked for. When the preferred side does not fit it tries the
 * opposite side, then the perpendicular ones (so a bubble beside a full-width
 * section moves above it instead of covering it), and only clamps the
 * preferred side when nothing fits.
 *
 * A bubble beside its anchor is centred on `hintRect` when the step dashes
 * one option (Figma 9430-3521: the arrow sits at the "neutral" row, not in
 * the middle of the open list), on the anchor otherwise. When sliding the
 * bubble into the viewport moves it off that centre, `tipOffset` keeps the
 * arrow pointing there (kept `COACH_MARK_TIP_INSET` away from the corners).
 */
export function computeCoachMarkPosition({
    rect,
    size,
    placement = 'bottom',
    align = 'center',
    viewport,
    hintRect = null,
}) {
    const vw = viewport?.width ?? window.innerWidth;
    const vh = viewport?.height ?? window.innerHeight;
    const minTop = (viewport?.top ?? 0) + COACH_MARK_MARGIN;
    const minLeft = (viewport?.left ?? 0) + COACH_MARK_MARGIN;
    const maxBottom = (viewport?.top ?? 0) + vh - COACH_MARK_MARGIN;
    const maxRight = (viewport?.left ?? 0) + vw - COACH_MARK_MARGIN;
    const centerX = rect.left + rect.width / 2;
    const pointAt = hintRect || rect;
    const centerY = pointAt.top + pointAt.height / 2;

    const horizontalLeft = () => {
        if (align === 'start') return centerX - COACH_MARK_TIP_INSET;
        if (align === 'end') return centerX + COACH_MARK_TIP_INSET - size.width;
        return centerX - size.width / 2;
    };

    const raw = (side) => {
        switch (side) {
            case 'top':
                return { top: rect.top - COACH_MARK_GAP - size.height, left: horizontalLeft() };
            case 'left':
                return { top: centerY - size.height / 2, left: rect.left - COACH_MARK_GAP - size.width };
            case 'right':
                return { top: centerY - size.height / 2, left: rect.left + rect.width + COACH_MARK_GAP };
            case 'bottom':
            default:
                return { top: rect.top + rect.height + COACH_MARK_GAP, left: horizontalLeft() };
        }
    };

    const fits = (side) => {
        const { top, left } = raw(side);
        return VERTICAL_SIDES.has(side)
            ? top >= minTop && top + size.height <= maxBottom
            : left >= minLeft && left + size.width <= maxRight;
    };

    const side = fits(placement) ? placement : (FALLBACKS[placement] || []).find(fits) || placement;
    const pos = raw(side);
    const top = Math.round(clamp(pos.top, minTop, maxBottom - size.height));
    const left = Math.round(clamp(pos.left, minLeft, maxRight - size.width));
    const tipOffset = VERTICAL_SIDES.has(side)
        ? null
        : Math.round(clamp(centerY - top, COACH_MARK_TIP_INSET, size.height - COACH_MARK_TIP_INSET));

    return { placement: side, top, left, tipOffset };
}

/**
 * Position a coach-mark bubble next to an anchor rect. Measures the bubble
 * after every render that changes the inputs (title/body length changes its
 * height) and returns `{ top, left, placement, tipOffset, ready }` in
 * viewport coordinates. Pass `viewport` (memoised by value — a fresh object
 * every render would defeat the re-measure cap below) to confine the bubble
 * to a box other than the window, and `hintRect` (same caveat) to centre a
 * beside-bubble on the step's dashed option instead of its anchor.
 */
export function useCoachMarkPosition({
    bubbleRef,
    rect,
    placement = 'bottom',
    align = 'center',
    viewport = null,
    hintRect = null,
}) {
    const [state, setState] = useState({ top: 0, left: 0, placement, tipOffset: null, ready: false });
    // The bubble's box depends on the side its tip is drawn on (the tip adds
    // padding there), and the tip follows the *applied* placement. When the
    // applied side changes, measure once more so the box matches the new tip.
    // One extra pass per input change: two sides that each only fit when
    // measured under the other's tip must not ping-pong forever.
    const passRef = useRef({ inputs: null, remeasured: false });

    useLayoutEffect(() => {
        const node = bubbleRef.current;
        if (!node || !rect) {
            setState((prev) => (prev.ready ? { ...prev, ready: false } : prev));
            return;
        }
        const inputs = [rect, placement, align, viewport, hintRect];
        const pass = passRef.current;
        if (pass.inputs?.every((value, i) => value === inputs[i])) {
            if (pass.remeasured) return;
            pass.remeasured = true;
        } else {
            passRef.current = { inputs, remeasured: false };
        }
        const size = { width: node.offsetWidth || 0, height: node.offsetHeight || 0 };
        const next = computeCoachMarkPosition({ rect, size, placement, align, viewport, hintRect });
        setState((prev) =>
            prev.ready &&
            prev.top === next.top &&
            prev.left === next.left &&
            prev.placement === next.placement &&
            prev.tipOffset === next.tipOffset
                ? prev
                : { ...next, ready: true }
        );
    }, [bubbleRef, rect, placement, align, viewport, hintRect, state.placement]);

    return state;
}

export default useCoachMarkPosition;
